/**
 * Ввод одного года истории — строка таблицы раскрывается НА МЕСТЕ.
 *
 * Никакого модального окна: соседние годы остаются перед глазами, потому что
 * агроном помнит историю как последовательность, а не как отдельные карточки.
 * Главная кнопка ведёт в следующий ПУСТОЙ год — за один заход закрывается вся
 * дырявая история без возврата в список.
 */
import { useMemo, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useFieldStates } from '@/lib/reference/fieldCatalogs';
import { fieldStateOptions } from '../constants';
import { cropPickerKey, useCropPicker, type CropPickerItem } from '../api';
import {
  TERMINATION_OPTIONS,
  canSaveYear,
  cropKeyFromEntry,
  checkArea,
  effectiveAreaHa,
  emptyEntry,
  fallowOptionValue,
  fmtHa,
  missingCropOption,
  occupationValue,
  parseOccupationValue,
  perennialOpenWarning,
  perennialStartYear,
  predecessorHint,
  switchUnit,
  sumCuts,
  type DraftEntry,
  type YearMode,
} from './rotationMath';
import type { RotationEntryBody, RotationYear, RotationYearBody } from './api';

interface Props {
  year: number;
  fieldAreaHa: number | null;
  /** Поле рисовой системы — тогда в списке занятий есть ещё и АМП. */
  isRiceSystem: boolean;
  existing: RotationYear | undefined;
  /** Куда ведёт главная кнопка. null — дырок в истории больше нет. */
  nextEmpty: number | null;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (body: RotationYearBody, goToYear: number | null) => void;
}

/** Сколько занятий было в году — развилка, с которой начинается ввод.
 *  Пара здесь НЕТ: он выбирается в той же выпадашке, что и культура. */
type CountChoice = 'one' | 'many';

function toDraft(entries: RotationYear['entries'] | undefined): DraftEntry[] {
  if (!entries?.length) return [emptyEntry()];
  return entries.map((e) => ({
    ...emptyEntry(),
    kind: e.kind,
    cropKey: cropKeyFromEntry(e),
    cropName: e.crop_name ?? undefined,
    fallowKind: e.fallow_kind ?? undefined,
    areaHa: e.area_ha,
    sharePct: e.share_pct,
    yieldCHa: e.yield_c_ha,
    sowingDate: e.sowing_date,
    harvestDate: e.harvest_date,
    // Многолетник поднимаем ЦЕЛИКОМ: без года посева правка завела бы новый
    // сев, без укосов — стёрла бы разбивку года.
    isPerennial: e.is_perennial,
    perennialStartYear: e.perennial_start_year,
    cutsCHa: e.cuts_c_ha ?? [],
    terminationKind: e.termination_kind,
    terminationDate: e.termination_date,
  }));
}

function num(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function RotationYearEditor({
  year,
  fieldAreaHa,
  isRiceSystem,
  existing,
  nextEmpty,
  saving,
  error,
  onCancel,
  onSave,
}: Props) {
  const picker = useCropPicker();
  const fieldStates = useFieldStates();
  // Поиск — СВОЙ у каждой строки года: один общий фильтр оставлял на части 2
  // то, что искали для части 1. Полноценный поиск внутри выпадашки придёт с
  // `ui/combobox` (заявка DESIGN-LEAD) — до него держим состояние строкой.
  const [search, setSearch] = useState<Record<number, string>>({});
  const [entries, setEntries] = useState<DraftEntry[]>(() => toDraft(existing?.entries));
  const [mode, setMode] = useState<YearMode>(() =>
    existing?.mode === 'parallel' || existing?.mode === 'sequential' ? existing.mode : 'single',
  );
  const [count, setCount] = useState<CountChoice>(() =>
    (existing?.entries?.length ?? 0) > 1 ? 'many' : 'one',
  );
  const [reason, setReason] = useState('');

  const wasImported = existing?.entries?.some((e) => e.source === 'import') ?? false;

  // Выпадашка занятия — алфавит + поиск, без «часто используемых» и топ-N
  // (сквозное правило продукта). Культуры и пар — ДВА РАЗДЕЛА одного списка.
  const allCrops: CropPickerItem[] = useMemo(
    () => [...(picker.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [picker.data],
  );

  const allFallows = useMemo(
    () => fieldStateOptions(fieldStates.data ?? [], isRiceSystem),
    [fieldStates.data, isRiceSystem],
  );

  function filtered(idx: number) {
    const q = (search[idx] ?? '').trim().toLowerCase();
    if (!q) return { crops: allCrops, fallows: allFallows };
    return {
      crops: allCrops.filter((c) => c.name.toLowerCase().includes(q)),
      fallows: allFallows.filter((f) => f.name.toLowerCase().includes(q)),
    };
  }

  const area = checkArea(entries, mode, fieldAreaHa);
  const verdict = canSaveYear(entries, mode, fieldAreaHa, year);
  const predecessor = predecessorHint(entries, mode);

  function patch(idx: number, next: Partial<DraftEntry>) {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...next } : e)));
  }

  /** Выбор в общей выпадашке: культура ИЛИ занятие без культуры (пар/АМП). */
  function pickOccupation(idx: number, value: string) {
    const item = (picker.data ?? []).find((c) => cropPickerKey(c) === value);
    // Культуры нет в списке хозяйства (сиротская строка) — имя берём из уже
    // записанного занятия, иначе предшественник года потеряет подпись.
    const kept = value === entries[idx]?.cropKey ? entries[idx].cropName : undefined;
    const next = parseOccupationValue(value, item?.name ?? kept);
    // Пар не бывает многолетним и урожайности не несёт — гасим оба поля сразу,
    // чтобы форма не хранила «хвост» от предыдущего выбора.
    patch(
      idx,
      next.kind === 'fallow'
        ? { ...next, yieldCHa: null, isPerennial: false, cutsCHa: [] }
        : next,
    );
  }

  function changeCount(next: CountChoice) {
    setCount(next);
    if (next === 'one') {
      setEntries((prev) => [prev[0] ?? emptyEntry()]);
      setMode('single');
      return;
    }
    setEntries((prev) => (prev.length > 1 ? prev : [...prev, emptyEntry()]));
    setMode('parallel');
  }

  function body(): RotationYearBody {
    const payload: RotationEntryBody[] = entries.map((e) => {
      // Строка пикера несёт ОБА id, и слать надо оба — как это делает соседний
      // SowForm. Разбор ключа «или canon, или номенклатура» отправлял бы
      // `crop_id: null` на культуру, которая в номенклатуре есть, а посев без
      // культуры нарушает ck_sowing_crop_xor_state.
      const item = (picker.data ?? []).find((c) => cropPickerKey(c) === e.cropKey);
      const cropId =
        item?.crop_id ?? (e.cropKey.startsWith('crop:') ? e.cropKey.slice(5) : null);
      const lci =
        item?.local_catalog_item_id ??
        (e.cropKey && !e.cropKey.startsWith('crop:') ? e.cropKey : null);
      return {
        kind: e.kind,
        crop_id: e.kind === 'crop' ? cropId : null,
        local_catalog_item_id: e.kind === 'crop' ? lci : null,
        fallow_kind: e.kind === 'fallow' ? (e.fallowKind ?? null) : null,
        siderat_crop_id: e.kind === 'fallow' ? cropId : null,
        area_ha: e.inputAs === 'ha' ? e.areaHa : null,
        share_pct: e.inputAs === 'pct' ? e.sharePct : null,
        // У многолетника урожайность года — СУММА укосов, а не отдельное поле:
        // иначе цифра из строки и цифра из укосов разошлись бы молча.
        yield_c_ha:
          e.kind === 'fallow' ? null : e.isPerennial ? sumCuts(e.cutsCHa) : e.yieldCHa,
        sowing_date: e.sowingDate,
        harvest_date: e.harvestDate,
        is_perennial: e.isPerennial,
        // Год посева уходит ВСЕГДА, когда занятие многолетнее: по нему сервер
        // находит переходящий посев и дописывает в него год. Без него каждая
        // правка заводила бы новый сев.
        perennial_start_year: e.isPerennial ? perennialStartYear(e, year) : null,
        cuts_c_ha: e.cutsCHa,
        termination_kind: e.terminationKind,
        termination_date: e.terminationDate,
      };
    });
    return {
      mode: entries.length > 1 ? mode : 'single',
      entries: payload,
      change_reason: reason.trim() || null,
    };
  }

  return (
    <div className="rounded-lg border-2 border-accent-brand bg-card p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{year} — что росло на поле</h3>
          <p className="text-xs text-muted-foreground">
            Поле {fmtHa(fieldAreaHa)} по эталонному контуру
            {predecessor ? ` · предшественник для ${year + 1} — ${predecessor}` : ''}
          </p>
        </div>
        <Badge variant="secondary">будет записано как «введено вручную»</Badge>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          Сколько занятий было в этом году
        </Label>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ['one', 'Одно'],
              ['many', 'Два и больше'],
            ] as Array<[CountChoice, string]>
          ).map(([value, label]) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={count === value ? 'default' : 'outline'}
              onClick={() => changeCount(value)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {count === 'many' && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Как они росли
          </Label>
          <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                [
                  'parallel',
                  'Одновременно — поле было поделено на части',
                  'Каждая культура на своём куске поля. Сумма частей не больше площади поля.',
                ],
                [
                  'sequential',
                  'Одна за другой — вторую сеяли после уборки первой',
                  'Пожнивной посев. Предшественником следующего года считается последняя.',
                ],
              ] as Array<[YearMode, string, string]>
            ).map(([value, title, hint]) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                className={cn(
                  'rounded-lg border p-3 text-left transition-colors',
                  mode === value ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted',
                )}
              >
                <span className="block text-sm font-medium">{title}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {entries.map((entry, idx) => (
          <div key={idx} className="rounded-md border border-border p-3 space-y-3">
            {entries.length > 1 && (
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">
                  {mode === 'sequential' ? `${idx + 1}-я по очереди` : `Часть ${idx + 1}`}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEntries((p) => p.filter((_, i) => i !== idx))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-4">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Чем занято поле
                </Label>
                <Input
                  value={search[idx] ?? ''}
                  onChange={(e) => setSearch((p) => ({ ...p, [idx]: e.target.value }))}
                  placeholder="Поиск по названию…"
                />
                {/* Культуры и пар/АМП — ОДИН список двумя разделами: агроном
                    ищет «Пар чёрный» там же, где «Подсолнечник», потому что для
                    него это один вопрос «чем занято поле». */}
                <Select value={occupationValue(entry)} onValueChange={(v) => pickOccupation(idx, v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Культура, пар или АМП…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Культуры</SelectLabel>
                      {/* Записанная культура, которой нет в текущем списке
                          хозяйства, показывается своей строкой: плейсхолдер
                          соврал бы, что поле пустое, и правка затёрла бы её. */}
                      {(() => {
                        const orphan = missingCropOption(
                          entry.cropKey,
                          entry.cropName,
                          allCrops.map(cropPickerKey),
                        );
                        return orphan ? (
                          <SelectItem value={orphan.value}>{orphan.label}</SelectItem>
                        ) : null;
                      })()}
                      {filtered(idx).crops.map((c) => (
                        <SelectItem key={cropPickerKey(c)} value={cropPickerKey(c)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Без культуры — занятие поля</SelectLabel>
                      {filtered(idx).fallows.map((o) => (
                        <SelectItem key={o.code} value={fallowOptionValue(o.code)}>
                          {o.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    {entry.inputAs === 'ha' ? 'Площадь' : '% засева'}
                  </Label>
                  <div className="flex overflow-hidden rounded-md border border-border">
                    {(['ha', 'pct'] as const).map((u) => (
                      <button
                        key={u}
                        type="button"
                        onClick={() =>
                          setEntries((p) =>
                            p.map((e, i) => (i === idx ? switchUnit(e, u, fieldAreaHa) : e)),
                          )
                        }
                        className={cn(
                          'px-2 py-1 text-xs font-semibold',
                          entry.inputAs === u
                            ? 'bg-foreground text-background'
                            : 'text-muted-foreground',
                        )}
                      >
                        {u === 'ha' ? 'га' : '%'}
                      </button>
                    ))}
                  </div>
                </div>
                <Input
                  inputMode="decimal"
                  value={
                    entry.inputAs === 'ha'
                      ? (entry.areaHa ?? '')
                      : (entry.sharePct ?? '')
                  }
                  onChange={(e) =>
                    patch(
                      idx,
                      entry.inputAs === 'ha'
                        ? { areaHa: num(e.target.value) }
                        : { sharePct: num(e.target.value) },
                    )
                  }
                  placeholder={entry.inputAs === 'ha' ? 'га' : '%'}
                />
                <p className="text-xs text-muted-foreground">
                  {entry.inputAs === 'pct'
                    ? `${fmtHa(effectiveAreaHa(entry, fieldAreaHa))} от поля`
                    : 'переключение пересчитывает, а не стирает'}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Урожайность
                </Label>
                <Input
                  inputMode="decimal"
                  disabled={entry.kind === 'fallow'}
                  value={entry.yieldCHa ?? ''}
                  onChange={(e) => patch(idx, { yieldCHa: num(e.target.value) })}
                  placeholder={entry.kind === 'fallow' ? 'не применимо' : 'ц/га'}
                />
                <p className="text-xs text-muted-foreground">
                  {entry.kind === 'fallow'
                    ? 'у пара урожайности не бывает'
                    : 'бункерный вес, ц/га · пусто = «не знаем», не ноль'}
                </p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Дата сева
                </Label>
                <Input
                  type="date"
                  value={entry.sowingDate ?? ''}
                  onChange={(e) => patch(idx, { sowingDate: e.target.value || null })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Дата уборки
                </Label>
                <Input
                  type="date"
                  disabled={entry.kind === 'fallow'}
                  value={entry.harvestDate ?? ''}
                  onChange={(e) => patch(idx, { harvestDate: e.target.value || null })}
                />
                <p className="text-xs text-muted-foreground">
                  {mode === 'sequential' && idx === 0
                    ? 'вторую культуру можно сеять только после этой даты'
                    : 'необязательно — точный день за давний год никто не вспомнит'}
                </p>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Статус посева
                </Label>
                <div className="flex h-9 items-center rounded-md border border-border bg-muted px-3 text-sm">
                  {entry.kind === 'fallow' ? 'занятие поля без культуры' : 'Убрано'}
                </div>
                <p className="text-xs text-muted-foreground">
                  архивный год — статус проставляется сразу, лестницу задним числом не проходим
                </p>
              </div>
            </div>

            {entry.kind === 'crop' && (
              <PerennialBlock
                entry={entry}
                year={year}
                onPatch={(next) => patch(idx, next)}
              />
            )}
          </div>
        ))}

        {count === 'many' && entries.length < 6 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setEntries((p) => [...p, emptyEntry()])}
          >
            <Plus className="mr-2 h-4 w-4" />
            Добавить занятие
          </Button>
        )}
      </div>

      {wasImported && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Почему правим · необязательно
          </Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="напр.: в старой системе стоял зачётный вес, у нас бункерный"
          />
          <p className="text-xs text-muted-foreground">
            После сохранения источник года сменится на «введено у нас». Импортное значение
            останется в истории — повторный импорт эту строку не перезатрёт.
          </p>
        </div>
      )}

      {(verdict.message || error) && (
        <Alert variant="destructive">
          <AlertDescription>{error ?? verdict.message}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
        <span className="text-xs text-muted-foreground">
          Занято <b className="text-foreground">{fmtHa(area.occupiedHa)}</b>
          {area.remainingHa != null && ` · остаток ${fmtHa(Math.max(0, area.remainingHa))}`}
        </span>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            Отмена
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!verdict.ok || saving}
            onClick={() => onSave(body(), null)}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Сохранить и закрыть
          </Button>
          {nextEmpty != null && (
            <Button
              type="button"
              disabled={!verdict.ok || saving}
              onClick={() => onSave(body(), nextEmpty)}
            >
              Сохранить и перейти к {nextEmpty} →
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Многолетние травы — раздел 6 wireframe.
 *
 * Люцерна не пересевается каждый год, но урожайность даёт каждый год. Если
 * заводить её отдельным посевом на каждый год, история соврёт: покажет четыре
 * сева там, где был один. Поэтому здесь три вещи, без которых переходящий
 * посев собрать нельзя: отметка «переходящий», ГОД ПОСЕВА (по нему сервер
 * находит тот же посев и дописывает в него год) и укосы этого года.
 */
function PerennialBlock({
  entry,
  year,
  onPatch,
}: {
  entry: DraftEntry;
  year: number;
  onPatch: (next: Partial<DraftEntry>) => void;
}) {
  const startYear = perennialStartYear(entry, year);
  const warning = perennialOpenWarning(entry);
  const total = sumCuts(entry.cutsCHa);

  return (
    <div className="rounded-md border border-dashed border-border p-3 space-y-3">
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={entry.isPerennial}
          onChange={(e) =>
            onPatch(
              e.target.checked
                ? { isPerennial: true, perennialStartYear: entry.perennialStartYear ?? year }
                : {
                    isPerennial: false,
                    perennialStartYear: null,
                    cutsCHa: [],
                    terminationKind: null,
                    terminationDate: null,
                  },
            )
          }
        />
        <span>
          <span className="block font-medium">Многолетние травы — переходящий посев</span>
          <span className="block text-xs text-muted-foreground">
            один сев на несколько лет; в каждом году свои укосы, а не новый сев
          </span>
        </span>
      </label>

      {entry.isPerennial && (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Год посева
              </Label>
              <Input
                inputMode="numeric"
                value={entry.perennialStartYear ?? ''}
                onChange={(e) => onPatch({ perennialStartYear: num(e.target.value) })}
                placeholder={String(year)}
              />
              <p className="text-xs text-muted-foreground">
                {startYear === year
                  ? '1-й год — травы посеяли в этом году'
                  : `${year - startYear + 1}-й год · сев был в ${startYear}`}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Чем закончился
              </Label>
              <Select
                value={entry.terminationKind ?? ''}
                onValueChange={(v) => onPatch({ terminationKind: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="ещё не закончился" />
                </SelectTrigger>
                <SelectContent>
                  {TERMINATION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Когда ушли с поля
              </Label>
              <Input
                type="date"
                disabled={!entry.terminationKind}
                value={entry.terminationDate ?? ''}
                onChange={(e) => onPatch({ terminationDate: e.target.value || null })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Урожайность за год
              </Label>
              <div className="flex h-9 items-center rounded-md border border-border bg-muted px-3 text-sm tabular-nums">
                {total ?? '—'}
              </div>
              <p className="text-xs text-muted-foreground">сумма укосов, ц/га</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Укосы этого года
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              {entry.cutsCHa.map((c, i) => (
                <div key={i} className="flex items-center gap-1">
                  <Input
                    inputMode="decimal"
                    className="w-24"
                    value={c}
                    onChange={(e) =>
                      onPatch({
                        cutsCHa: entry.cutsCHa.map((v, j) =>
                          j === i ? (num(e.target.value) ?? 0) : v,
                        ),
                      })
                    }
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      onPatch({ cutsCHa: entry.cutsCHa.filter((_, j) => j !== i) })
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onPatch({ cutsCHa: [...entry.cutsCHa, 0] })}
              >
                <Plus className="mr-2 h-4 w-4" />
                {entry.cutsCHa.length ? `${entry.cutsCHa.length + 1}-й укос` : 'Добавить укос'}
              </Button>
            </div>
          </div>

          {warning && (
            <Alert>
              <AlertDescription className="text-xs">{warning}</AlertDescription>
            </Alert>
          )}
        </>
      )}
    </div>
  );
}
